import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

export default function AdminHero() {
  const [heroes, setHeroes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentHero, setCurrentHero] = useState(null);
  
  const [title, setTitle] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    fetchHeroes();
  }, []);

  const fetchHeroes = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('hero_sections').select('*');
      if (error) throw error;
      setHeroes(data || []);
    } catch (error) {
      console.error("Error fetching heroes:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCurrentHero(null);
    setTitle('');
    setImageFile(null);
    setImageUrl('');
    setIsActive(false);
    setIsEditing(false);
  };

  const handleEdit = (hero) => {
    setCurrentHero(hero);
    setTitle(hero.title || '');
    setImageUrl(hero.image_url || '');
    setIsActive(hero.active || false);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this hero section?')) {
      try {
        const { error } = await supabase.from('hero_sections').delete().eq('id', id);
        if (error) throw error;
        fetchHeroes();
      } catch (error) {
        alert('Error: ' + error.message);
      }
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) return imageUrl;
    const fileExt = imageFile.name.split('.').pop();
    const fileName = `${Math.random()}.${fileExt}`;
    const filePath = `hero/${fileName}`;

    try {
      const { error: uploadError } = await supabase.storage
        .from('website-assets')
        .upload(filePath, imageFile);
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('website-assets').getPublicUrl(filePath);
      return data.publicUrl;
    } catch (error) {
      alert('Upload Error: ' + error.message);
      return imageUrl;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let finalImageUrl = imageUrl;
      if (imageFile) {
        finalImageUrl = await handleImageUpload();
      }

      // If this one is active, make others inactive (optional logic, but good practice)
      if (isActive) {
        await supabase.from('hero_sections').update({ active: false }).neq('id', '00000000-0000-0000-0000-000000000000'); // update all
      }

      const heroData = { title, image_url: finalImageUrl, active: isActive };

      if (currentHero) {
        const { error } = await supabase.from('hero_sections').update(heroData).eq('id', currentHero.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('hero_sections').insert([heroData]);
        if (error) throw error;
      }
      resetForm();
      fetchHeroes();
    } catch (error) {
      alert('Save Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-properties">
      {isEditing ? (
        <div className="admin-form-container">
          <h3>{currentHero ? 'Edit Hero Section' : 'Add Hero Section'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Title</label>
              <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ margin: 0 }}>Set as Active Hero Banner?</label>
              <input type="checkbox" style={{ width: 'auto' }} checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
            </div>

            <div className="form-group">
              <label>Image</label>
              <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
              {imageUrl && !imageFile && <img src={imageUrl} alt="Preview" className="image-preview" />}
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={resetForm}>Cancel</button>
              <button type="submit" className="btn-save" disabled={loading}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="admin-list-container">
          <button className="btn-add" onClick={() => setIsEditing(true)}>+ Add Hero</button>
          
          {loading ? (
            <p>Loading...</p>
          ) : heroes.length === 0 ? (
            <p>No heroes found. Add one!</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {heroes.map((hero) => (
                  <tr key={hero.id}>
                    <td>
                      {hero.image_url ? (
                        <img src={hero.image_url} alt={hero.title} className="table-img" />
                      ) : 'No Image'}
                    </td>
                    <td>{hero.title}</td>
                    <td>{hero.active ? <strong style={{color:'green'}}>Active</strong> : 'Inactive'}</td>
                    <td>
                      <button className="btn-edit" onClick={() => handleEdit(hero)}>Edit</button>
                      <button className="btn-delete" onClick={() => handleDelete(hero.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
